// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type {
  SubscriptionsApplyPromoCodeRequest,
  SubscriptionsMutateCustomerRequest,
  SubscriptionsUpdateSubscriptionRequest,
} from '../types';
import type { MutationOptions } from '../utils/mutations';
import {
  deleteSubscriptionMutationKey,
  deleteSubscriptionPromoCodeMutationKey,
  postCustomerMutationKey,
  postSetupIntentMutationKey,
  postSubscriptionPromoCodeMutationKey,
  putCustomerMutationKey,
  putSubscriptionMutationKey,
} from './keys';
import {
  deleteSubscription,
  deleteSubscriptionPromoCode,
  postCustomer,
  postSetupIntent,
  postSubscriptionPromoCode,
  putCustomer,
  putSubscription,
} from './requests';

export type PutSubscriptionMutationVariables = {
  workspace_id: number;
  data: SubscriptionsUpdateSubscriptionRequest;
};

export type PutSubscriptionMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof putSubscription,
    PutSubscriptionMutationVariables,
    TContext
  >;

/**
 * Updates workspace subscription by calling toggl subscription service and updating our db records
 */
export function usePutSubscriptionMutation<TContext = unknown>(
  options: PutSubscriptionMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, data }) =>
      putSubscription(httpClient, workspace_id, data),
    mutationKey: putSubscriptionMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending putSubscription mutation. */
export function usePutSubscriptionMutationVariables() {
  return useMutationState({
    filters: { mutationKey: putSubscriptionMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PutSubscriptionMutationVariables,
  });
}

export type DeleteSubscriptionMutationVariables = { workspace_id: number };

export type DeleteSubscriptionMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteSubscription,
    DeleteSubscriptionMutationVariables,
    TContext
  >;

/**
 * Cancels workspace subscription by calling toggl subscription service and updating our db records
 */
export function useDeleteSubscriptionMutation<TContext = unknown>(
  options: DeleteSubscriptionMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id }) =>
      deleteSubscription(httpClient, workspace_id),
    mutationKey: deleteSubscriptionMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteSubscription mutation. */
export function useDeleteSubscriptionMutationVariables() {
  return useMutationState({
    filters: { mutationKey: deleteSubscriptionMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as DeleteSubscriptionMutationVariables,
  });
}

export type PutCustomerMutationVariables = {
  workspace_id: number;
  data: SubscriptionsMutateCustomerRequest;
};

export type PutCustomerMutationOptions<TContext = unknown> = MutationOptions<
  typeof putCustomer,
  PutCustomerMutationVariables,
  TContext
>;

/**
 * Replaces the current workspace customer that is used by toggl subscription service in stripe
 */
export function usePutCustomerMutation<TContext = unknown>(
  options: PutCustomerMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, data }) =>
      putCustomer(httpClient, workspace_id, data),
    mutationKey: putCustomerMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending putCustomer mutation. */
export function usePutCustomerMutationVariables() {
  return useMutationState({
    filters: { mutationKey: putCustomerMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PutCustomerMutationVariables,
  });
}

export type PostCustomerMutationVariables = {
  workspace_id: number;
  data: SubscriptionsMutateCustomerRequest;
};

export type PostCustomerMutationOptions<TContext = unknown> = MutationOptions<
  typeof postCustomer,
  PostCustomerMutationVariables,
  TContext
>;

/**
 * Creates a workspace customer in toggl's subscriptions service that, in turn creates a customer in stripe
 */
export function usePostCustomerMutation<TContext = unknown>(
  options: PostCustomerMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, data }) =>
      postCustomer(httpClient, workspace_id, data),
    mutationKey: postCustomerMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postCustomer mutation. */
export function usePostCustomerMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postCustomerMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PostCustomerMutationVariables,
  });
}

export type PostSetupIntentMutationVariables = { workspace_id: number };

export type PostSetupIntentMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postSetupIntent,
    PostSetupIntentMutationVariables,
    TContext
  >;

/**
 * Starts a intent in toggl's subscription service for the workspace's organization
 */
export function usePostSetupIntentMutation<TContext = unknown>(
  options: PostSetupIntentMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id }) => postSetupIntent(httpClient, workspace_id),
    mutationKey: postSetupIntentMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postSetupIntent mutation. */
export function usePostSetupIntentMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postSetupIntentMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PostSetupIntentMutationVariables,
  });
}

export type PostSubscriptionPromoCodeMutationVariables = {
  workspace_id: number;
  data: SubscriptionsApplyPromoCodeRequest;
};

export type PostSubscriptionPromoCodeMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postSubscriptionPromoCode,
    PostSubscriptionPromoCodeMutationVariables,
    TContext
  >;

/**
 * Applies a promo code to a customer subscription
 */
export function usePostSubscriptionPromoCodeMutation<TContext = unknown>(
  options: PostSubscriptionPromoCodeMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, data }) =>
      postSubscriptionPromoCode(httpClient, workspace_id, data),
    mutationKey: postSubscriptionPromoCodeMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postSubscriptionPromoCode mutation. */
export function usePostSubscriptionPromoCodeMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: postSubscriptionPromoCodeMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as PostSubscriptionPromoCodeMutationVariables,
  });
}

export type DeleteSubscriptionPromoCodeMutationVariables = {
  workspace_id: number;
};

export type DeleteSubscriptionPromoCodeMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof deleteSubscriptionPromoCode,
    DeleteSubscriptionPromoCodeMutationVariables,
    TContext
  >;

/**
 * Delete the current customer promo code
 */
export function useDeleteSubscriptionPromoCodeMutation<TContext = unknown>(
  options: DeleteSubscriptionPromoCodeMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id }) =>
      deleteSubscriptionPromoCode(httpClient, workspace_id),
    mutationKey: deleteSubscriptionPromoCodeMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending deleteSubscriptionPromoCode mutation. */
export function useDeleteSubscriptionPromoCodeMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: deleteSubscriptionPromoCodeMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as DeleteSubscriptionPromoCodeMutationVariables,
  });
}
